import { Injectable } from '@angular/core'
import { BehaviorSubject, EMPTY, Observable, Subject, timer } from 'rxjs'
import { catchError, filter, takeUntil, tap } from 'rxjs/operators'
import { webSocket, WebSocketSubject } from 'rxjs/webSocket'
import { environment } from 'src/environments/environment'
import { DdcLoggerService } from '../log/ddc-logger.service'
import { UserService } from '../user-info/user.service'

export interface WebSocketMessage {
  type: string
  data?: any
  timestamp?: string
  source?: WebSocketType
}

export enum WebSocketType {
  GENERAL = 'general',
  USER_SPECIFIC = 'user_specific',
}

@Injectable({
  providedIn: 'root',
})
export class WebSocketService {
  private _username: string = ''

  // Connection sockets
  private generalSocket$: WebSocketSubject<any> | null = null
  private userSocket$: WebSocketSubject<any> | null = null

  // Connection status subjects
  private generalConnectionStatus$ = new BehaviorSubject<boolean>(false)
  private userConnectionStatus$ = new BehaviorSubject<boolean>(false)

  // Message subjects
  private generalMessagesSubject$ = new Subject<WebSocketMessage>()
  private userMessagesSubject$ = new Subject<WebSocketMessage>()

  // Configuration
  private reconnectInterval = 5000 // 5 seconds
  private maxReconnectAttempts = 5
  private generalReconnectAttempts = 0
  private userReconnectAttempts = 0
  private destroy$ = new Subject<void>()

  // Public observables - combined messages from both sockets
  public messages$ = new BehaviorSubject<WebSocketMessage[]>([])
  public generalMessages$ = this.generalMessagesSubject$.asObservable()
  public userMessages$ = this.userMessagesSubject$.asObservable()
  public isGeneralConnected$ = this.generalConnectionStatus$.asObservable()
  public isUserConnected$ = this.userConnectionStatus$.asObservable()
  public isConnected$ = new BehaviorSubject<boolean>(false)

  // Canvas run events (filtered from user messages)
  public canvasRunEvents$ = this.userMessagesSubject$.pipe(filter(msg => msg.type === 'canvas_run'))

  // get current username

  constructor(
    private logger: DdcLoggerService,
    private user_info: UserService,
  ) {
    this.user_info.userInfo$.pipe(takeUntil(this.destroy$)).subscribe((res: any) => {
      if (res && res.username) {
        this._username = res.username
      }
    })
    // Start both connections when service initializes
    this.initializeConnections()

    // Subscribe to both message streams and combine them
    this.setupMessageHandling()

    // Monitor overall connection status
    this.setupConnectionStatusMonitoring()
  }

  /**
   * Initialize both WebSocket connections
   */
  private initializeConnections(): void {
    console.log('WebSocketService: Initializing both connections...')
    this.connectGeneral()
    this.user_info.userInfo$.pipe(takeUntil(this.destroy$)).subscribe((res: any) => {
      if (res && res.username) {
        this._username = res.username
        this.connectUserSpecific()
      }
    })
  }

  /**
   * Setup message handling for both streams
   */
  private setupMessageHandling(): void {
    // Combine messages from both streams
    this.generalMessages$.pipe(takeUntil(this.destroy$)).subscribe(message => {
      this.addMessageToHistory(message, WebSocketType.GENERAL)
    })

    this.userMessages$.pipe(takeUntil(this.destroy$)).subscribe(message => {
      this.addMessageToHistory(message, WebSocketType.USER_SPECIFIC)
    })
  }

  /**
   * Monitor overall connection status
   */
  private setupConnectionStatusMonitoring(): void {
    // Update overall connection status based on both connections
    this.generalConnectionStatus$
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => this.updateOverallConnectionStatus())

    this.userConnectionStatus$
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => this.updateOverallConnectionStatus())
  }

  /**
   * Update overall connection status
   */
  private updateOverallConnectionStatus(): void {
    const isConnected = this.generalConnectionStatus$.value || this.userConnectionStatus$.value
    this.isConnected$.next(isConnected)
  }

  /**
   * Add message to history with source information
   */
  private addMessageToHistory(message: WebSocketMessage, source: WebSocketType): void {
    const currentMessages = this.messages$.value
    const messageWithSource = {
      ...message,
      source: source,
      timestamp: message.timestamp || new Date().toISOString(),
    }

    // Keep only last 100 messages to prevent memory issues
    const updatedMessages = [...currentMessages, messageWithSource].slice(-100)
    this.messages$.next(updatedMessages)
  }

  /**
   * Establish general WebSocket connection
   */
  private connectGeneral(): void {
    console.log('WebSocketService: Initializing general connection...')
    if (this.generalSocket$) {
      return
    }

    const wsUrl = this.getWebSocketUrl('general')
    console.log('WebSocketService: Attempting to connect to general:', wsUrl)

    this.generalSocket$ = webSocket({
      url: wsUrl,
      openObserver: {
        next: () => {
          console.log('WebSocketService: General connection opened')
          this.generalConnectionStatus$.next(true)
          this.generalReconnectAttempts = 0

          // Send a test ping message to verify connection
          this.sendGeneralMessage({
            type: 'ping',
            data: { message: 'General connection test' },
            timestamp: new Date().toISOString(),
          })
        },
      },
      closeObserver: {
        next: () => {
          console.log('WebSocketService: General connection closed')
          this.generalConnectionStatus$.next(false)
          this.generalSocket$ = null
          this.scheduleReconnect('general')
        },
      },
    })

    // Subscribe to incoming messages
    this.generalSocket$
      .pipe(
        tap((message: WebSocketMessage) => {
          console.log('WebSocketService: General message received:', message)
          this.generalMessagesSubject$.next(message)
        }),
        catchError((error): Observable<never> => {
          console.error('WebSocketService: General connection error:', error)
          this.generalConnectionStatus$.next(false)
          this.generalSocket$ = null
          this.scheduleReconnect('general')
          return EMPTY
        }),
        takeUntil(this.destroy$),
      )
      .subscribe()
  }

  /**
   * Establish user-specific WebSocket connection
   */
  private connectUserSpecific(): void {
    console.log('WebSocketService: Initializing user-specific connection...')
    if (this.userSocket$) {
      return
    }
    // get the current username

    const wsUrl = this.getWebSocketUrl('user' + '/' + this._username)

    console.log('WebSocketService: Attempting to connect to user-specific:', wsUrl)

    this.userSocket$ = webSocket({
      url: wsUrl,
      openObserver: {
        next: () => {
          console.log('WebSocketService: User-specific connection opened')
          this.userConnectionStatus$.next(true)
          this.userReconnectAttempts = 0

          // Send a test ping message to verify connection
          this.sendUserMessage({
            type: 'ping',
            data: { message: 'User-specific connection test' },
            timestamp: new Date().toISOString(),
          })
        },
      },
      closeObserver: {
        next: (event: any) => {
          console.log('WebSocketService: User-specific connection closed', event)
          this.userConnectionStatus$.next(false)
          this.userSocket$ = null

          // Check if it's an authentication error
          if (event && event.code === 4001) {
            console.warn(
              'WebSocketService: User-specific connection closed due to authentication failure',
            )
            // Don't auto-reconnect for auth failures
            return
          }

          this.scheduleReconnect('user')
        },
      },
    })

    // Subscribe to incoming messages
    this.userSocket$
      .pipe(
        tap((message: WebSocketMessage) => {
          console.log('WebSocketService: User-specific message received:', message)
          this.userMessagesSubject$.next(message)
        }),
        catchError((error): Observable<never> => {
          console.error('WebSocketService: User-specific connection error:', error)
          this.userConnectionStatus$.next(false)
          this.userSocket$ = null
          this.scheduleReconnect('user')
          return EMPTY
        }),
        takeUntil(this.destroy$),
      )
      .subscribe()
  }

  /**
   * Get the WebSocket URL based on environment and endpoint
   */
  private getWebSocketUrl(endpoint: string): string {
    const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:'
    const host = window.location.host

    if (environment.production) {
      return `${protocol}//${host}/ws/${endpoint}/`
    } else {
      // For development with SSL, use secure WebSocket
      return `wss://localhost:8000/ws/${endpoint}/`
    }
  }

  /**
   * Schedule a reconnection attempt
   */
  private scheduleReconnect(connectionType: 'general' | 'user'): void {
    const isGeneral = connectionType === 'general'
    const currentAttempts = isGeneral ? this.generalReconnectAttempts : this.userReconnectAttempts

    if (currentAttempts >= this.maxReconnectAttempts) {
      console.log(`WebSocketService: Max ${connectionType} reconnection attempts reached`)
      return
    }

    if (isGeneral) {
      this.generalReconnectAttempts++
    } else {
      this.userReconnectAttempts++
    }

    const attempts = isGeneral ? this.generalReconnectAttempts : this.userReconnectAttempts
    console.log(
      `WebSocketService: Scheduling ${connectionType} reconnection attempt ${attempts}/${this.maxReconnectAttempts}`,
    )

    timer(this.reconnectInterval)
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        if (isGeneral) {
          this.connectGeneral()
        } else {
          this.connectUserSpecific()
        }
      })
  }

  /**
   * Send a message through the general WebSocket
   */
  public sendGeneralMessage(message: WebSocketMessage): void {
    if (this.generalSocket$ && this.generalConnectionStatus$.value) {
      this.generalSocket$.next(message)
      console.log('WebSocketService: Sent general message:', message)
    } else {
      console.log('WebSocketService: Cannot send general message - not connected')
    }
  }

  /**
   * Send a message through the user-specific WebSocket
   */
  public sendUserMessage(message: WebSocketMessage): void {
    if (this.userSocket$ && this.userConnectionStatus$.value) {
      this.userSocket$.next(message)
      console.log('WebSocketService: Sent user message:', message)
    } else {
      console.log('WebSocketService: Cannot send user message - not connected')
    }
  }

  /**
   * Send a message through the appropriate WebSocket (backward compatibility)
   */
  public sendMessage(
    message: WebSocketMessage,
    socketType: WebSocketType = WebSocketType.GENERAL,
  ): void {
    if (socketType === WebSocketType.USER_SPECIFIC) {
      this.sendUserMessage(message)
    } else {
      this.sendGeneralMessage(message)
    }
  }

  /**
   * Get current connection status for specific socket
   */
  public isConnected(socketType?: WebSocketType): boolean {
    if (socketType === WebSocketType.GENERAL) {
      return this.generalConnectionStatus$.value
    } else if (socketType === WebSocketType.USER_SPECIFIC) {
      return this.userConnectionStatus$.value
    }
    // Return true if either connection is active
    return this.isConnected$.value
  }

  /**
   * Manually disconnect specific or all sockets
   */
  public disconnect(socketType?: WebSocketType): void {
    if (!socketType || socketType === WebSocketType.GENERAL) {
      if (this.generalSocket$) {
        this.generalSocket$.complete()
        this.generalSocket$ = null
      }
      this.generalConnectionStatus$.next(false)
    }

    if (!socketType || socketType === WebSocketType.USER_SPECIFIC) {
      if (this.userSocket$) {
        this.userSocket$.complete()
        this.userSocket$ = null
      }
      this.userConnectionStatus$.next(false)
    }
  }

  /**
   * Manually reconnect specific or all sockets
   */
  public reconnect(socketType?: WebSocketType): void {
    if (!socketType || socketType === WebSocketType.GENERAL) {
      this.disconnect(WebSocketType.GENERAL)
      this.generalReconnectAttempts = 0
      this.connectGeneral()
    }

    if (!socketType || socketType === WebSocketType.USER_SPECIFIC) {
      this.disconnect(WebSocketType.USER_SPECIFIC)
      this.userReconnectAttempts = 0
      this.connectUserSpecific()
    }
  }

  /**
   * Clean up on service destruction
   */
  public destroy(): void {
    this.destroy$.next()
    this.destroy$.complete()
    this.disconnect()
  }
}
